import { api } from 'src/boot/axios';
import { defineStore } from 'pinia';
import {
  TNormalizedOrganisation,
  TNormalizedRegion,
  TRegionDataDictionary,
  TRegionTypeDataDictionary,
} from 'types';
import { getResponseData } from 'utils';

type State = {
  regions: TNormalizedRegion[];
  dataDictionary: TRegionDataDictionary[];
  regionTypeDataDictionary: TRegionTypeDataDictionary[];
};

export const useRegionsStore = defineStore('regions', {
  state: (): State => ({
    regions: [],
    dataDictionary: [],
    regionTypeDataDictionary: [],
  }),
  getters: {
    regionTypesByOrganisation:
      (state) =>
      (organisationId: number): TRegionTypeDataDictionary[] => {
        return state.regionTypeDataDictionary
          .filter((regionType) => regionType.organisation_id === organisationId)
          .sort((a, b) => a.region_type_name.localeCompare(b.region_type_name));
      },
    regionsByOrganisation:
      (state) =>
      (
        organisationId: number,
        regionTypeId?: number
      ): TRegionDataDictionary[] => {
        return state.dataDictionary
          .filter((region) => {
            const matchesOrg = region.organisation_id === organisationId;
            const matchesType =
              regionTypeId === undefined ||
              region.region_type_id === regionTypeId;
            return matchesOrg && matchesType;
          })
          .sort((a, b) => a.region_name.localeCompare(b.region_name));
      },
    regionsByRegionType:
      (state) =>
      (regionTypeId: number): TRegionDataDictionary[] => {
        return state.dataDictionary
          .filter((region) => region.region_type_id === regionTypeId)
          .sort((a, b) => a.region_name.localeCompare(b.region_name));
      },
    regionsByName:
      (state) =>
      (search: string, regionTypeId?: number): TRegionDataDictionary[] => {
        const needle = search.toLowerCase();
        return state.dataDictionary
          .filter((region) => {
            const matchesName = region.region_name
              .toLowerCase()
              .includes(needle);
            const matchesType =
              regionTypeId === undefined ||
              region.region_type_id === regionTypeId;
            return matchesName && matchesType;
          })
          .sort((a, b) => a.region_name.localeCompare(b.region_name));
      },
  },
  actions: {
    async getRegion(id: number): Promise<TNormalizedRegion> {
      const response = await api.get(`/api/v1/regions/${id}`);
      return getResponseData(response);
    },

    extractDataDictionaries(organisations: TNormalizedOrganisation[]) {
      const regionEntries: TRegionDataDictionary[] = [];
      const regionTypeEntries: TRegionTypeDataDictionary[] = [];
      const seenRegionTypes = new Set<string>();

      for (const org of organisations) {
        if (!org.regions) continue;

        for (const region of org.regions) {
          const regionType = region as TNormalizedRegion & {
            region_type_id?: number;
            region_type_name?: string;
            region_type_short_name?: string;
            abbr?: string;
            region_type_label?: string;
            groups?: { id: number; name: string }[];
          };

          // Extract region data dictionary
          regionEntries.push({
            id: region.id,
            organisation_id: org.id,
            region_name: region.region_name ?? '',
            region_short_name: region.region_short_name ?? '',
            abbr: region.abbr ?? '',
            region_type_label: regionType.region_type_label ?? '',
            region_type_id: regionType.region_type_id ?? 0,
            groups: (regionType.groups ?? []).map((g) => ({
              id: Number(g.id),
              name: g.name,
            })),
          });

          // Extract unique region type data dictionary
          const regionTypeKey = `${org.id}-${regionType.region_type_id}`;
          if (
            regionType.region_type_id &&
            !seenRegionTypes.has(regionTypeKey)
          ) {
            seenRegionTypes.add(regionTypeKey);
            regionTypeEntries.push({
              id: regionType.region_type_id,
              organisation_id: org.id,
              region_type_name: regionType.region_type_name ?? '',
              region_type_short_name: regionType.region_type_short_name ?? '',
              abbr: regionType.abbr,
            });
          }
        }
      }

      this.dataDictionary = regionEntries;
      this.regionTypeDataDictionary = regionTypeEntries;
    },

    clear() {
      this.regions = [];
      this.dataDictionary = [];
      this.regionTypeDataDictionary = [];
    },
  },
});
